import { useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
  kstDotDateStr,
} from '@biznomi/ui-react'
import { useAuth } from '@/lib/auth/store'
import { getEmployees } from '@/lib/api/admin'
import {
  cancelContract,
  fileSizeText,
  getContracts,
  openContractFile,
  uploadContract,
  type AdminContract,
  type ContractStatus,
} from '@/lib/api/contract'

/**
 * 근로계약서 관리.
 *
 * 우리가 하는 일은 **전달·보관·증빙**뿐이다 (§1). 계약서를 만들어 주지도,
 * 내용이 법에 맞는지 봐주지도 않는다. 회사가 만든 PDF 를 그대로 받아
 * 바뀌지 않게 보관하고, 본인만 열 수 있게 하고, 서명 사실을 남긴다.
 *
 * 발송·회수는 ADMIN 전용. 부서장은 담당 부서 현황을 보기만 한다 (서버도 동일).
 */

const ALL = '__all__'
const PAGE_SIZE = 20

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: ALL, label: '전체' },
  { value: 'PENDING', label: '미열람' },
  { value: 'VIEWED', label: '서명 대기' },
  { value: 'SIGNED', label: '서명 완료' },
  { value: 'CANCELLED', label: '회수됨' },
]

function StatusBadge({ s }: { s: ContractStatus }) {
  if (s === 'SIGNED') return <Badge variant="info">서명 완료</Badge>
  if (s === 'VIEWED') return <Badge variant="warning">서명 대기</Badge>
  if (s === 'CANCELLED') return <Badge variant="outline">회수됨</Badge>
  return <Badge variant="outline">미열람</Badge>
}

/** 없으면 '—'. 있으면 KST 기준 날짜 — 기기 타임존을 따르면 해외·시계 오차에서 어긋난다 */
function dateText(iso: string | null): string {
  return iso ? kstDotDateStr(iso) : '—'
}

export default function AdminContracts() {
  const qc = useQueryClient()
  const isAdmin = useAuth((s) => s.user?.role) === 'ADMIN'

  const [page, setPage] = useState(1)
  const [status, setStatus] = useState(ALL)
  const [search, setSearch] = useState('')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<AdminContract | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', 'admin', page, status, search],
    queryFn: () =>
      getContracts({
        page,
        limit: PAGE_SIZE,
        status: status === ALL ? undefined : (status as ContractStatus),
        search: search || undefined,
      }),
  })

  const result = data?.success ? data.data : undefined
  const rows = result?.items ?? []
  const total = result?.total ?? 0

  async function view(c: AdminContract) {
    setOpening(c.id)
    setError('')
    const res = await openContractFile(c.id)
    setOpening(null)
    if ('error' in res) {
      setError(res.error)
      return
    }
    const win = window.open(res.url, '_blank')
    if (!win) window.location.href = res.url
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  async function doCancel() {
    if (!cancelTarget) return
    const res = await cancelContract(cancelTarget.id)
    setCancelTarget(null)
    if (!res.success) {
      setError(res.error ?? '회수에 실패했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['contracts', 'admin'] })
    setToast('계약서를 회수했습니다')
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">근로계약서</h1>
        {isAdmin && <Button size="sm" onClick={() => setUploadOpen(true)}>계약서 올리기</Button>}
      </div>

      <InfoBox tone="info">
        계약서 PDF 를 올리면 직원이 읽고 서명합니다. 파일은 본인과 관리자만 열 수 있고,
        누가 언제 열어 봤는지 남습니다. 계약서 내용은 저희가 쓰지도, 봐 주지도 않습니다.
      </InfoBox>

      <div className="flex flex-wrap gap-2">
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="직원 이름 · 제목 검색"
          className="w-56"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="계약서가 없습니다"
          hint={isAdmin ? '«계약서 올리기» 로 시작하세요.' : undefined}
        />
      ) : (
        <>
          <ul className="divide-y divide-slate-100 rounded-card bg-white">
            {rows.map((c) => (
              <li key={c.id} className="space-y-2 px-4 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {c.employee.name}
                      <span className="ml-1.5 text-xs font-normal text-slate-400">
                        {[c.employee.employeeNumber, c.employee.department?.name]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </p>
                    <p className="mt-0.5 truncate text-sm text-slate-600">{c.title}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {c.fileName} · {fileSizeText(c.fileSize)} · 발송 {dateText(c.createdAt)}
                      {c.signedAt
                        ? ` · 서명 ${dateText(c.signedAt)}`
                        : c.viewedAt
                          ? ` · 열람 ${dateText(c.viewedAt)}`
                          : ''}
                    </p>
                  </div>
                  <StatusBadge s={c.status} />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={opening === c.id}
                    onClick={() => void view(c)}
                  >
                    보기
                  </Button>
                  {isAdmin && c.status !== 'SIGNED' && c.status !== 'CANCELLED' && (
                    <Button variant="secondary" size="sm" onClick={() => setCancelTarget(c)}>
                      회수
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <Pagination
            page={page}
            totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
            onChange={setPage}
          />
        </>
      )}

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onDone={(name) => {
            setUploadOpen(false)
            qc.invalidateQueries({ queryKey: ['contracts', 'admin'] })
            setToast(`${name} 에게 계약서를 보냈습니다`)
          }}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setCancelTarget(null)}
          title="계약서를 회수할까요?"
          message={`${cancelTarget.employee.name} 님 «${cancelTarget.title}» 을 회수합니다. 직원 화면에서 사라지고 서명도 못 하게 됩니다.`}
          confirmText="회수"
          destructive
          onConfirm={() => void doCancel()}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

function UploadDialog({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (employeeName: string) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [employeeId, setEmployeeId] = useState('')
  const [title, setTitle] = useState('')
  const [memo, setMemo] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const { data: empData } = useQuery({
    queryKey: ['employees', 'for-contract'],
    queryFn: () => getEmployees({ take: 500, status: 'active' }),
  })
  const employees = useMemo(
    () => (empData?.success ? empData.data?.employees ?? [] : []),
    [empData],
  )
  const selected = employees.find((e) => e.id === employeeId)

  async function submit() {
    if (!employeeId) return setError('직원을 선택해 주세요')
    if (!title.trim()) return setError('제목을 입력해 주세요')
    if (!file) return setError('PDF 파일을 선택해 주세요')

    setSaving(true)
    setError('')
    const res = await uploadContract({ employeeId, title: title.trim(), memo: memo.trim() || undefined, file })
    setSaving(false)
    if (!res.success) {
      setError(res.error ?? '올리지 못했습니다')
      return
    }
    onDone(selected?.name ?? '직원')
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>계약서 올리기</DialogTitle>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>직원</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="직원 선택" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                    {e.employeeNumber ? ` (${e.employeeNumber})` : ''}
                    {e.department?.name ? ` · ${e.department.name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contract-title">제목</Label>
            <Input
              id="contract-title"
              placeholder="2026년 근로계약서"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contract-file">파일 (PDF, 최대 10MB)</Label>
            <input
              id="contract-file"
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <p className="text-xs text-slate-400">{file.name} · {fileSizeText(file.size)}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contract-memo">메모 (직원에게 보이지 않음)</Label>
            <Input
              id="contract-memo"
              placeholder="선택 입력"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </div>

          {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button className="flex-1" loading={saving} onClick={() => void submit()}>
              보내기
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
